import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchStatistics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatistics, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/statistics');
      setStatistics(response.data.statistics);
      setLastUpdate(new Date());
      setError('');
    } catch (err) {
      console.error('Error fetching statistics:', err);
      if (err.response?.status === 403) {
        setError('У вас нет прав доступа к админ-панели');
      } else if (err.response?.status === 401) {
        setError('Требуется авторизация');
      } else {
        setError(err.response?.data?.message || 'Ошибка загрузки статистики');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ru-RU').format(num);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading && !statistics) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">
          <div>Загрузка статистики...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="admin-error">
          <h2>Ошибка</h2>
          <p>{error}</p>
          <button onClick={fetchStatistics}>Попробовать снова</button>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return null;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Админ-панель</h1>
        <div className="admin-actions">
          <button onClick={fetchStatistics} className="btn-refresh">
            🔄 Обновить
          </button>
          {lastUpdate && (
            <span className="last-update">
              Обновлено: {formatDate(lastUpdate)}
            </span>
          )}
        </div>
      </div>

      <div className="admin-stats-grid">
        {/* Users Statistics */}
        <div className="stat-card">
          <div className="stat-card-header">
            <h2>👥 Пользователи</h2>
          </div>
          <div className="stat-card-content">
            <div className="stat-item">
              <span className="stat-label">Всего пользователей:</span>
              <span className="stat-value">{formatNumber(statistics.users.total)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Зарегистрировано сегодня:</span>
              <span className="stat-value highlight">{formatNumber(statistics.users.today)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">За эту неделю:</span>
              <span className="stat-value">{formatNumber(statistics.users.thisWeek)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">За этот месяц:</span>
              <span className="stat-value">{formatNumber(statistics.users.thisMonth)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">С балансом > 10 ₽:</span>
              <span className="stat-value">{formatNumber(statistics.users.withBalance)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Совершили платежи:</span>
              <span className="stat-value">{formatNumber(statistics.users.withPayments)}</span>
            </div>
          </div>
        </div>

        {/* Subscriptions Statistics */}
        <div className="stat-card">
          <div className="stat-card-header">
            <h2>📋 Подписки</h2>
          </div>
          <div className="stat-card-content">
            <div className="stat-item">
              <span className="stat-label">Активных подписок:</span>
              <span className="stat-value highlight">{formatNumber(statistics.subscriptions.active)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Всего платежей:</span>
              <span className="stat-value">{formatNumber(statistics.subscriptions.total)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Платежей сегодня:</span>
              <span className="stat-value highlight">{formatNumber(statistics.subscriptions.today)}</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-label">По статусу:</span>
            </div>
            <div className="stat-item nested">
              <span className="stat-label">• Активные:</span>
              <span className="stat-value">{formatNumber(statistics.subscriptions.byStatus.active)}</span>
            </div>
            <div className="stat-item nested">
              <span className="stat-label">• Истекшие:</span>
              <span className="stat-value">{formatNumber(statistics.subscriptions.byStatus.expired)}</span>
            </div>
            <div className="stat-item nested">
              <span className="stat-label">• Отмененные:</span>
              <span className="stat-value">{formatNumber(statistics.subscriptions.byStatus.cancelled)}</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-label">По тарифу:</span>
            </div>
            <div className="stat-item nested">
              <span className="stat-label">• Free:</span>
              <span className="stat-value">{formatNumber(statistics.subscriptions.byPlan.free)}</span>
            </div>
            <div className="stat-item nested">
              <span className="stat-label">• Basic:</span>
              <span className="stat-value">{formatNumber(statistics.subscriptions.byPlan.basic)}</span>
            </div>
            <div className="stat-item nested">
              <span className="stat-label">• Premium:</span>
              <span className="stat-value">{formatNumber(statistics.subscriptions.byPlan.premium)}</span>
            </div>
            <div className="stat-item nested">
              <span className="stat-label">• Enterprise:</span>
              <span className="stat-value">{formatNumber(statistics.subscriptions.byPlan.enterprise)}</span>
            </div>
          </div>
        </div>

        {/* Financial Statistics */}
        <div className="stat-card">
          <div className="stat-card-header">
            <h2>💰 Финансы</h2>
          </div>
          <div className="stat-card-content">
            <div className="stat-item">
              <span className="stat-label">Общий баланс:</span>
              <span className="stat-value highlight large">{formatCurrency(statistics.financial.totalBalance)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Средний баланс:</span>
              <span className="stat-value">{formatCurrency(statistics.financial.averageBalance)}</span>
            </div>
          </div>
        </div>

        {/* Referral Statistics */}
        <div className="stat-card">
          <div className="stat-card-header">
            <h2>🎁 Реферальная программа</h2>
          </div>
          <div className="stat-card-content">
            <div className="stat-item">
              <span className="stat-label">Пользователей с рефералами:</span>
              <span className="stat-value">{formatNumber(statistics.referrals.usersWithReferrals)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Пользователей, которые пригласили:</span>
              <span className="stat-value">{formatNumber(statistics.referrals.usersWhoReferred)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Users */}
      <div className="stat-card full-width">
        <div className="stat-card-header">
          <h2>🆕 Последние пользователи</h2>
        </div>
        <div className="stat-card-content">
          <div className="recent-users-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Телефон</th>
                  <th>Баланс</th>
                  <th>Статус</th>
                  <th>Дата регистрации</th>
                </tr>
              </thead>
              <tbody>
                {statistics.recentUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.phone || '-'}</td>
                    <td>{formatCurrency(parseFloat(u.balance || 0))}</td>
                    <td>
                      <span className={`status-badge status-${u.subscriptionStatus}`}>
                        {u.subscriptionStatus === 'active' ? 'Активна' : 
                         u.subscriptionStatus === 'expired' ? 'Истекла' : 'Отменена'}
                      </span>
                    </td>
                    <td>{formatDate(u.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

